'use client'
import React from 'react'
import { createToastHook } from '@gluestack-ui/core/toast/creator'
import { AccessibilityInfo, Text, View, ViewStyle } from 'react-native'
import { tva } from '../../tailwind/merge'
import { cssInterop } from 'nativewind'
import { Motion, AnimatePresence, MotionComponentProps } from '@legendapp/motion'
import { withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'

type IMotionViewProps = React.ComponentProps<typeof View> &
    MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>

const MotionView = Motion.View as React.ComponentType<IMotionViewProps>

const useToast = createToastHook(MotionView, AnimatePresence)
const SCOPE = 'TOAST'

cssInterop(MotionView, { className: 'style' })

// AOS redesign: remap to status tokens (the gluestack defaults used unmapped
// `bg-error-800` / `border-outline-100` etc. so toasts rendered colourless).
// `solid` = filled status bg + dark text; `outline` = opaque elevated surface
// (toasts float, so no translucent tint) + status border + light text.
const toastStyle = tva({
    base: 'p-4 m-1 rounded-md gap-1 web:pointer-events-auto shadow-hard-5',
    variants: {
        action: {
            error: 'border-status-critical',
            warning: 'border-status-warning',
            success: 'border-status-success',
            info: 'border-status-info',
            muted: 'border-status-neutral',
        },

        variant: {
            solid: '',
            outline: 'border bg-background-elevation',
        },
    },
    compoundVariants: [
        { action: 'error', variant: 'solid', class: 'bg-status-critical' },
        { action: 'warning', variant: 'solid', class: 'bg-status-warning' },
        { action: 'success', variant: 'solid', class: 'bg-status-success' },
        { action: 'info', variant: 'solid', class: 'bg-status-info' },
        { action: 'muted', variant: 'solid', class: 'bg-status-neutral' },
    ],
})

const toastTitleStyle = tva({
    base: 'font-medium font-body tracking-md text-left',
    variants: {
        isTruncated: {
            true: '',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            'sm': 'text-sm',
            'md': 'text-base',
            'lg': 'text-lg',
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
    },
    parentVariants: {
        // Solid → dark on-graphics text (on the bright fill); outline → light
        // default text (on the dark elevated surface).
        variant: {
            solid: 'text-content-inverted',
            outline: 'text-content-default',
        },
    },
})

const toastDescriptionStyle = tva({
    base: 'font-normal font-body tracking-md text-left',
    variants: {
        isTruncated: {
            true: '',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            'sm': 'text-sm',
            'md': 'text-base',
            'lg': 'text-lg',
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
    },
    parentVariants: {
        variant: {
            solid: 'text-content-inverted',
            outline: 'text-content-default',
        },
    },
})

const Root = withStyleContext(View, SCOPE)
type IToastProps = React.ComponentProps<typeof Root> & {
    className?: string
} & VariantProps<typeof toastStyle>

const Toast = React.forwardRef<React.ComponentRef<typeof Root>, IToastProps>(function Toast(
    { className, variant = 'solid', action = 'muted', ...props },
    ref,
) {
    return (
        <Root
            ref={ref}
            className={toastStyle({ variant, action, class: className })}
            context={{ variant, action }}
            {...props}
        />
    )
})

type IToastTitleProps = React.ComponentProps<typeof Text> & {
    className?: string
} & VariantProps<typeof toastTitleStyle>

const ToastTitle = React.forwardRef<React.ComponentRef<typeof Text>, IToastTitleProps>(
    function ToastTitle({ className, size = 'md', children, ...props }, ref) {
        const { variant: parentVariant } = useStyleContext(SCOPE)
        React.useEffect(() => {
            // Issue from react-native side
            // Hack for now, will fix this later
            AccessibilityInfo.announceForAccessibility(children as string)
        }, [children])

        return (
            <Text
                {...props}
                ref={ref}
                aria-live='assertive'
                aria-atomic='true'
                role='alert'
                className={toastTitleStyle({
                    size,
                    class: className,
                    parentVariants: {
                        variant: parentVariant,
                    },
                })}
            >
                {children}
            </Text>
        )
    },
)

type IToastDescriptionProps = React.ComponentProps<typeof Text> & {
    className?: string
} & VariantProps<typeof toastDescriptionStyle>

const ToastDescription = React.forwardRef<React.ComponentRef<typeof Text>, IToastDescriptionProps>(
    function ToastDescription({ className, size = 'md', ...props }, ref) {
        const { variant: parentVariant } = useStyleContext(SCOPE)
        return (
            <Text
                ref={ref}
                {...props}
                className={toastDescriptionStyle({
                    size,
                    class: className,
                    parentVariants: {
                        variant: parentVariant,
                    },
                })}
            />
        )
    },
)

Toast.displayName = 'Toast'
ToastTitle.displayName = 'ToastTitle'
ToastDescription.displayName = 'ToastDescription'

export { useToast, Toast, ToastTitle, ToastDescription }
